// Client for /api/onboarding (server/src/routes/onboarding.ts): the setup
// wizard and the dashboard checklist. Shapes mirror the server's view().

import type {
  ChecklistItem,
  InstallState,
  OnboardingChannel,
  OnboardingGoal,
  OnboardingPlatform,
  OnboardingStep,
  OnboardingSteps,
  OnboardingTeamSize,
} from '@prakash-software/shared';
import { apiFetch, isApiError } from './api';

export interface OnboardingState {
  profile: { workspaceName: string; website: string | null; industry: string | null };
  answers: {
    goal: OnboardingGoal | null;
    teamSize: OnboardingTeamSize | null;
    platform: OnboardingPlatform | null;
    channels: OnboardingChannel[];
  };
  steps: OnboardingSteps;
  nextStep: OnboardingStep | null;
  version: number;
  canEdit: boolean;
  site: {
    id: string;
    name: string;
    origin: string | null;
    embedToken: string;
    install: { state: InstallState; lastSeenAt: string | null; seenOrigin: string | null; checkedAt: string | null };
  } | null;
  activatedAt: string | null;
  checklist: ChecklistItem[];
}

/** Null for an account that didn't sign up self-serve (no wizard). */
export async function fetchOnboarding(): Promise<OnboardingState | null> {
  return (await apiFetch<{ onboarding: OnboardingState | null }>('/api/onboarding')).onboarding;
}

/** A write lost to a newer save (another tab, another admin). Carries the
 *  current state so the wizard can show it instead of the stale one. */
export class StaleOnboardingError extends Error {
  constructor(readonly current: OnboardingState) {
    super('stale');
  }
}

async function write(path: string, body: unknown): Promise<OnboardingState> {
  try {
    return (await apiFetch<{ onboarding: OnboardingState }>(path, { method: 'PATCH', body: JSON.stringify(body) })).onboarding;
  } catch (err) {
    const current = isApiError(err) && err.status === 409 ? (err.body as { onboarding?: OnboardingState }).onboarding : undefined;
    if (current) throw new StaleOnboardingError(current);
    throw err;
  }
}

export function saveProfile(
  input: { workspaceName: string; website: string | null; industry: string | null },
  version: number,
): Promise<OnboardingState> {
  return write('/api/onboarding/profile', { ...input, version });
}

export function saveStep(
  step: OnboardingStep,
  status: 'completed' | 'skipped',
  version: number,
  value?: unknown,
): Promise<OnboardingState> {
  return write('/api/onboarding/progress', { step, status, version, ...(value !== undefined ? { value } : {}) });
}

export async function checkInstall(): Promise<{
  state: InstallState;
  lastSeenAt: string | null;
  seenOrigin: string | null;
  hasOrigin: boolean;
}> {
  return apiFetch('/api/onboarding/install-check', { method: 'POST' });
}

export async function sendTestMessage(): Promise<string> {
  return (await apiFetch<{ sessionId: string }>('/api/onboarding/test-message', { method: 'POST' })).sessionId;
}

/** Appearance the wizard edits, through the site's existing widget settings. */
export async function saveWidgetAppearance(
  siteId: string,
  patch: { chatTitle: string | null; greeting: string | null; accentColor: string; bubblePosition: 'bottom-right' | 'bottom-left' },
): Promise<void> {
  await apiFetch(`/api/admin/sites/${siteId}/widget-config`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function loadWidgetAppearance(siteId: string) {
  const { widgetConfig } = await apiFetch<{
    widgetConfig: { chatTitle: string | null; greeting: string | null; accentColor: string; bubblePosition: string };
  }>(`/api/admin/sites/${siteId}/widget-config`);
  return widgetConfig;
}
